import { useEffect, useRef } from "react";
import Quill from "quill";
import QuillMarkdown from "quilljs-markdown";
import "quill/dist/quill.snow.css";
import "quilljs-markdown/dist/quilljs-markdown-common-style.css";
import "./richtext.css";

// --- Emit inline styles (not ql-* classes) so the stored HTML renders anywhere
// (e.g. the public site) without needing Quill's stylesheet. Registered once.
let registered = false;
const SIZE_WHITELIST = ["12px", "14px", "16px", "18px", "20px", "24px", "32px"];
const FONT_WHITELIST = ["", "serif", "monospace"];
const registerFormats = () => {
  if (registered) return;
  const Size = Quill.import("attributors/style/size");
  Size.whitelist = SIZE_WHITELIST;
  Quill.register(Size, true);
  const Font = Quill.import("attributors/style/font");
  Font.whitelist = FONT_WHITELIST;
  Quill.register(Font, true);
  Quill.register(Quill.import("attributors/style/align"), true);
  Quill.register(Quill.import("attributors/style/color"), true);
  Quill.register(Quill.import("attributors/style/background"), true);
  Quill.register(Quill.import("attributors/style/direction"), true);
  registered = true;
};

// No code-block: a wellness blog has no use for it and the black code panel
// confused reviewers (QA 2026-07-27).
const TOOLBAR = [
  // `false` = the default body size; without it the picker showed "12px" as if
  // the text were already 12px.
  [{ font: FONT_WHITELIST }, { size: [false, ...SIZE_WHITELIST] }],
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["blockquote"],
  ["link", "image", "video"],
  ["clean"],
];

// Hover hints — the toolbar is icon-only and gave no clue what each button does.
const TOOLTIPS = {
  ".ql-picker.ql-font": "Font",
  ".ql-picker.ql-size": "Text size",
  ".ql-picker.ql-header": "Heading level",
  ".ql-bold": "Bold",
  ".ql-italic": "Italic",
  ".ql-underline": "Underline",
  ".ql-strike": "Strikethrough",
  ".ql-picker.ql-color": "Text colour",
  ".ql-picker.ql-background": "Highlight colour",
  '.ql-list[value="ordered"]': "Numbered list",
  '.ql-list[value="bullet"]': "Bulleted list",
  ".ql-picker.ql-align": "Text alignment",
  ".ql-blockquote": "Quote",
  ".ql-link": "Insert link",
  ".ql-image": "Insert image",
  ".ql-video": "Embed video (YouTube / Vimeo)",
  ".ql-clean": "Clear formatting",
};

const applyTooltips = (toolbarEl) => {
  if (!toolbarEl) return;
  Object.entries(TOOLTIPS).forEach(([selector, text]) => {
    toolbarEl.querySelectorAll(selector).forEach((el) => el.setAttribute("title", text));
  });
};

// Quill's default uploader reads dropped/pasted image files with
// readAsDataURL and embeds the base64 straight into the document. A single
// phone photo then makes the saved post several MB — past the reverse proxy's
// body limit, so Save died with a bare 413 and the editor could only say
// "Failed to save" (reported 2026-07-30). Every image goes to S3 instead, and
// the body carries a URL.
const dataUrlToFile = (dataUrl, name) => {
  const [meta, encoded] = String(dataUrl).split(",");
  const type = (meta.match(/data:([^;]+)/) || [])[1] || "image/png";
  const bytes = atob(encoded);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) buf[i] = bytes.charCodeAt(i);
  const ext = type.split("/")[1] || "png";
  return new File([buf], `${name}.${ext}`, { type });
};

const uploadFailure = (e) =>
  e?.response?.status === 413
    ? "That image is too large to upload. Please use a smaller file (under about 5 MB)."
    : e?.response?.data?.message || "Image upload failed. Please try again.";

const RichTextEditor = ({ value, onChange, uploadImage, onError }) => {
  const holderRef = useRef(null);
  const quillRef = useRef(null);
  // Keep latest callbacks without re-initializing Quill.
  const onChangeRef = useRef(onChange);
  const uploadRef = useRef(uploadImage);
  const onErrorRef = useRef(onError);
  onChangeRef.current = onChange;
  uploadRef.current = uploadImage;
  onErrorRef.current = onError;

  useEffect(() => {
    if (quillRef.current) return;
    registerFormats();

    // Replace an image embed in place, addressing it through Quill so the
    // document model stays in sync (a raw src swap on the DOM gets lost).
    const swapEmbed = (quill, node, url) => {
      const blot = Quill.find(node);
      if (!blot) return;
      const index = quill.getIndex(blot);
      quill.deleteText(index, 1, "user");
      if (url) quill.insertEmbed(index, "image", url, "user");
    };

    // Anything that slipped in as base64 (pasted HTML from Word / Docs, or an
    // older draft) gets lifted to S3 the moment it lands in the editor.
    const liftingDataUrls = new Set();
    const liftDataUrlImages = (quill) => {
      const upload = uploadRef.current;
      if (!upload) return;
      quill.root.querySelectorAll('img[src^="data:"]').forEach((node) => {
        const src = node.getAttribute("src");
        if (!src || liftingDataUrls.has(src)) return;
        liftingDataUrls.add(src);
        Promise.resolve()
          .then(() => upload(dataUrlToFile(src, `pasted-${Date.now()}`)))
          .then((url) => {
            if (url) swapEmbed(quill, node, url);
            else onErrorRef.current?.("Image upload failed. Please try again.");
          })
          .catch((e) => onErrorRef.current?.(uploadFailure(e)))
          .finally(() => liftingDataUrls.delete(src));
      });
    };

    // Drops and image pastes: upload, then embed the URL.
    const uploadFiles = async (quill, range, files) => {
      const upload = uploadRef.current;
      const images = Array.from(files || []).filter((f) => f.type?.startsWith("image/"));
      if (!images.length) return;
      let index = range?.index ?? quill.getLength();
      for (const file of images) {
        const placeholder = "  Uploading image…";
        quill.insertText(index, placeholder, "italic", true, "user");
        let url = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          url = upload ? await upload(file) : null;
        } catch (e) {
          url = null;
          onErrorRef.current?.(uploadFailure(e));
        }
        quill.deleteText(index, placeholder.length, "user");
        if (url) {
          quill.insertEmbed(index, "image", url, "user");
          index += 1;
          quill.setSelection(index, 0, "user");
        }
      }
    };

    const quill = new Quill(holderRef.current, {
      theme: "snow",
      placeholder: "Write your post…",
      modules: {
        toolbar: TOOLBAR,
        uploader: {
          mimetypes: ["image/png", "image/jpeg", "image/webp", "image/gif"],
          handler(range, files) {
            uploadFiles(this.quill, range, files);
          },
        },
      },
    });
    // eslint-disable-next-line no-new
    new QuillMarkdown(quill, {});

    // Upload content images to S3 and embed the returned URL (not base64).
    const imageHandler = () => {
      const upload = uploadRef.current;
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.click();
      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file) return;
        const sel = quill.getSelection(true);
        const index = sel ? sel.index : quill.getLength();
        const placeholder = "  Uploading image…";
        quill.insertText(index, placeholder, "italic", true, "user");
        let url = null;
        try {
          url = upload ? await upload(file) : null;
        } catch (e) {
          url = null;
          onErrorRef.current?.(uploadFailure(e));
        }
        quill.deleteText(index, placeholder.length, "user");
        if (url) {
          quill.insertEmbed(index, "image", url, "user");
          quill.setSelection(index + 1, 0, "user");
        } else {
          onErrorRef.current?.("Image upload failed. Please try again.");
        }
      };
    };
    quill.getModule("toolbar").addHandler("image", imageHandler);
    applyTooltips(quill.getModule("toolbar").container);

    if (value) quill.clipboard.dangerouslyPasteHTML(value);
    quill.on("text-change", (_delta, _old, source) => {
      liftDataUrlImages(quill);
      if (source === "user") onChangeRef.current(quill.root.innerHTML);
    });
    liftDataUrlImages(quill); // an existing draft may already hold base64
    quillRef.current = quill;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={holderRef} />;
};

export default RichTextEditor;
